// Generated from scripts/slideshow.ts. Rebuild with pnpm scripts:build.
var _a;
import { Component } from '@theme/component';
import { center, clamp, closest, getDirectionMultiplier, mediaQueryLarge, prefersReducedMotion, scheduler, viewTransition, } from '@theme/utilities';
import { Scroller, scrollIntoView } from '@theme/scrolling';
import { SlideshowSelectEvent } from '@theme/events';
/** Share of a slide that must sit inside the scroller for the slide to count as visible. */
const SLIDE_VISIBILITY_THRESHOLD = 0.7;
/** Drag distance (px) and release speed (px/s) below which a drag settles on its nearest slide. */
const DRAG_SETTLE_DISTANCE = 10;
const DRAG_SETTLE_VELOCITY = 10;
/** Release speed is measured over the moves in this last part of a drag (ms). */
const RELEASE_VELOCITY_WINDOW = 100;
/** Glide distance (px) per px/s of release speed after a free-scroll drag. */
const GLIDE_PER_VELOCITY = 0.2;
/** Share of a drag past either end that the strip follows, and the farthest (px) it stretches. */
const EDGE_RESISTANCE = 0.3;
const EDGE_STRETCH_LIMIT = 80;
/** Wheel travel (px) that makes one wheel gesture one step, and the quiet time (ms) that ends the gesture. */
const WHEEL_STEP_DISTANCE = 40;
const WHEEL_GESTURE_GAP = 180;
/** Pixels per line for wheels that report lines instead of pixels. */
const WHEEL_LINE_HEIGHT = 16;
/** Elements inside a slide that keyboard navigation moves focus between. */
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
/** Elements that use arrow keys themselves. */
const ARROW_KEY_OWNERS = 'input, textarea, select, video, audio, model-viewer, [role="slider"], [contenteditable]:not([contenteditable="false"])';
/** Everything inside a slide that can take keyboard focus, whatever its tabindex. */
const FOCUS_TARGETS = 'a[href], area[href], button, input:not([type="hidden"]), select, textarea, summary, iframe, [tabindex], [contenteditable]:not([contenteditable="false"])';
/** What a shopper can tab to: the focus targets that a tabindex of -1 does not take out of the sequence. */
const TAB_STOPS = [
    'a[href]',
    'area[href]',
    'button:not(:disabled)',
    'input:not(:disabled):not([type="hidden"])',
    'select:not(:disabled)',
    'textarea:not(:disabled)',
    'summary',
    'iframe',
    '[tabindex]',
    '[contenteditable]:not([contenteditable="false"])',
]
    .map((selector) => `${selector}:not([tabindex="-1"])`)
    .join(', ');
/** Release speed in px/s in scroll direction, from the samples inside the release window. */
function releaseVelocity(samples, now) {
    const recent = samples.filter((sample) => now - sample.time <= RELEASE_VELOCITY_WINDOW);
    const first = recent[0];
    const last = recent.at(-1);
    if (!first || !last || last.time === first.time)
        return 0;
    return ((first.position - last.position) / (last.time - first.time)) * 1000;
}
let viewportObserver;
/**
 * Enables scrolling only for slideshows near the viewport. iOS Safari crashes when a page holds too
 * many compositor layers, and a slideshow without `in-viewport` keeps `overflow: hidden`, which
 * creates none.
 */
function observeViewport(slideshow) {
    viewportObserver ??= new IntersectionObserver((entries) => {
        for (const entry of entries)
            entry.target.toggleAttribute('in-viewport', entry.isIntersecting);
    }, { rootMargin: '100px' });
    viewportObserver.observe(slideshow);
}
function unobserveViewport(slideshow) {
    viewportObserver?.unobserve(slideshow);
    slideshow.removeAttribute('in-viewport');
}
function isHTMLElement(value) {
    return value instanceof HTMLElement;
}
/**
 * Cancels the click that ends a press which dragged a strip, so releasing a dragged slide does not
 * follow the link or run the handler under the pointer, however the drag itself ended.
 */
function suppressNextClick() {
    // The block lasts until the click that ends this drag, or until the next press when none comes (the button was
    // released off the page). A timer would race a click the browser delivers in a later task, which then opens the
    // card under the pointer.
    const controller = new AbortController();
    const { signal } = controller;
    const block = (event) => {
        event.preventDefault();
        event.stopPropagation();
        controller.abort();
    };
    window.addEventListener('click', block, { capture: true, signal });
    window.addEventListener('pointerdown', () => controller.abort(), { capture: true, signal });
}
/** The tabindex a focus target had before a hidden slide took it out of the tab sequence, and the slides holding it out. */
const tabStopHolds = new WeakMap();
/**
 * Takes a slide's focus targets out of the tab sequence while the slide is hidden from assistive technology, and gives
 * them back when it is shown. A hidden slide is `aria-hidden`, which must not hold anything focusable (WCAG 4.1.2: a
 * screen reader user would land on an element that has no name for them), yet its links and buttons stay clickable
 * where the slide peeks into the strip. A target keeps the tabindex it had (or none) until the last slide holding it
 * lets go, so a slideshow inside a hidden slide and the slide around it never undo each other. A slide that is itself
 * a focus target, such as a linked text, is held like one inside it. Idempotent.
 */
function setTabStops(slide, tabbable) {
    if (slide.matches(FOCUS_TARGETS))
        holdTabStop(slide, slide, !tabbable);
    for (const target of slide.querySelectorAll(FOCUS_TARGETS))
        holdTabStop(target, slide, !tabbable);
}
/** One focus target held out of the tab sequence by `holder`, or given back when the last holder lets go. */
function holdTabStop(target, holder, hold) {
    const held = tabStopHolds.get(target);
    if (!hold) {
        if (!held?.holders.delete(holder) || held.holders.size > 0)
            return;
        tabStopHolds.delete(target);
        if (held.saved === null)
            target.removeAttribute('tabindex');
        else
            target.setAttribute('tabindex', held.saved);
    }
    else if (held) {
        held.holders.add(holder);
        // A morph can hand the server's tabindex back to an element that is still held.
        if (target.getAttribute('tabindex') !== '-1')
            target.setAttribute('tabindex', '-1');
    }
    else if (target.getAttribute('tabindex') !== '-1') {
        tabStopHolds.set(target, { saved: target.getAttribute('tabindex'), holders: new Set([holder]) });
        target.setAttribute('tabindex', '-1');
    }
}
/**
 * Slides between children of a native scroll-snap strip.
 *
 * The strip scrolls without this element, so slides stay reachable before the module runs. This
 * element adds selection through arrows, dots, thumbnails, the keyboard and the theme editor, mouse
 * dragging with an elastic edge, autoplay, the infinite loop and the visibility state that screen
 * readers and controls read.
 *
 * Opt-in attributes: `free-scroll` lets the strip rest between slides and glide after a drag;
 * `wheel` turns a vertical mouse wheel into horizontal travel until either end, where the page
 * takes over again. A `progress` ref receives the scroll position as `--slideshow-progress` and the
 * visible share as `--slideshow-progress-size`, both from 0 to 1.
 */
export class Slideshow extends Component {
    static get observedAttributes() {
        return ['initial-slide'];
    }
    requiredRefs = ['scroller'];
    #lifetime;
    #scroll;
    #resizeObserver;
    #intersectionObserver;
    #visibleSlides = [];
    #current = 0;
    #interval;
    /** Set while the infinite loop reorders slides; selection waits for it. */
    #looping = false;
    #dragging = false;
    /** Set while keyboard navigation moves focus, so the focus handler does not reposition again. */
    #keyboardFocus = false;
    #wheelTravel = 0;
    #wheelStepped = false;
    #wheelTimer;
    #progressFrame;
    attributeChangedCallback(name, oldValue, newValue) {
        // Collection filtering morphs galleries in place, updating `initial-slide` and the slides'
        // `hidden` attributes. Select after the morph, once the slides carry their new state.
        if (name !== 'initial-slide' || oldValue === newValue)
            return;
        queueMicrotask(() => {
            if (!this.isConnected || !this.#scroll)
                return;
            const index = parseInt(newValue ?? '', 10) || 0;
            const id = this.#slideRefs[index]?.getAttribute('slide-id');
            if (id)
                this.select({ id }, undefined, { animate: false });
        });
    }
    async connectedCallback() {
        super.connectedCallback();
        this.#lifetime?.abort();
        const lifetime = new AbortController();
        this.#lifetime = lifetime;
        observeViewport(this);
        if (viewTransition.current) {
            await viewTransition.current;
            if (lifetime.signal.aborted)
                return;
        }
        if ((this.slides?.length ?? 0) <= 1)
            this.#setupWithoutControls();
        else
            this.#setup(lifetime.signal);
        this.addEventListener('focusin', this.#handleFocusIn, { signal: lifetime.signal });
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.#lifetime?.abort();
        this.#lifetime = undefined;
        unobserveViewport(this);
        this.suspend();
        window.clearTimeout(this.#wheelTimer);
        if (this.#progressFrame !== undefined)
            cancelAnimationFrame(this.#progressFrame);
        this.#progressFrame = undefined;
        this.#scroll?.destroy();
        this.#scroll = undefined;
        this.#resizeObserver?.disconnect();
        this.#resizeObserver = undefined;
        this.#intersectionObserver?.disconnect();
        this.#intersectionObserver = undefined;
        this.#visibleSlides = [];
    }
    /** Whether the slideshow sits inside another slideshow. */
    get isNested() {
        return this.parentElement?.closest('slideshow-component') != null;
    }
    get initialSlide() {
        return this.#slideRefs[this.initialSlideIndex];
    }
    /** The 0-based index of the slide to show first, from `initial-slide`. */
    get initialSlideIndex() {
        const value = this.getAttribute('initial-slide');
        return value == null ? 0 : parseInt(value, 10);
    }
    /** Slides that take part in sliding: not hidden, or revealed for selection. */
    get slides() {
        return this.refs.slides === undefined
            ? undefined
            : this.#slideRefs.filter((slide) => !slide.hasAttribute('hidden') || slide.hasAttribute('reveal'));
    }
    get visibleSlides() {
        return this.#visibleSlides;
    }
    get current() {
        return this.#current;
    }
    /** Records the current slide and updates the counter, the selected dot or thumbnail and the arrows. */
    set current(value) {
        this.#current = value;
        const counter = this.refs.current;
        if (isHTMLElement(counter))
            counter.textContent = `${value + 1}`;
        for (const controls of [this.#refList('thumbnails'), this.#refList('dots')]) {
            // Dots and thumbnails are plain buttons, which take aria-current, not aria-selected.
            controls.forEach((control, index) => control.setAttribute('aria-current', `${index === value}`));
        }
        this.#updateArrows();
    }
    get infinite() {
        return this.hasAttribute('infinite');
    }
    /** Whether the strip rests wherever it stops instead of snapping to a slide. */
    get freeScroll() {
        return this.hasAttribute('free-scroll');
    }
    /** The index a step back reaches: one page when several slides are visible, else one slide. */
    get previousIndex() {
        return this.#current - this.#stepSize;
    }
    get nextIndex() {
        return this.#current + this.#stepSize;
    }
    get atStart() {
        return this.slides?.length ? this.#current === 0 : false;
    }
    get atEnd() {
        const slides = this.slides;
        return slides?.length ? this.#current === slides.length - 1 : false;
    }
    /** Whether sliding is off: set explicitly, nested in another slideshow, or `mobile-disabled` below the large breakpoint. */
    get disabled() {
        return this.getAttribute('disabled') === 'true' || (this.hasAttribute('mobile-disabled') && !mediaQueryLarge.matches);
    }
    set disabled(value) {
        this.setAttribute('disabled', String(value));
    }
    get autoplay() {
        return Boolean(this.autoplayInterval);
    }
    /** The autoplay interval in milliseconds, from `autoplay` in seconds. */
    get autoplayInterval() {
        const value = parseInt(this.getAttribute('autoplay') ?? '', 10);
        return Number.isNaN(value) ? undefined : value * 1000;
    }
    get paused() {
        return this.hasAttribute('paused');
    }
    set paused(value) {
        this.toggleAttribute('paused', value);
    }
    /**
     * Selects a slide by index, by index string from an `on:` action, or by `slide-id`. Selecting a
     * hidden slide by id reveals it until the next selection.
     */
    async select(input, event, options = {}) {
        if (this.#looping || !this.#slideRefs.length || !this.#scroll)
            return;
        const scroll = this.#scroll;
        const currentSlide = this.slides?.[this.#current];
        for (const slide of this.#slideRefs) {
            if (!slide.hasAttribute('reveal'))
                continue;
            slide.removeAttribute('reveal');
            slide.setAttribute('aria-hidden', 'true');
            setTabStops(slide, false);
        }
        let requestedIndex = this.#resolveIndex(input);
        const slides = this.slides;
        if (!slides?.length || requestedIndex === undefined || Number.isNaN(requestedIndex))
            return;
        if (currentSlide === slides[requestedIndex])
            return;
        if (!this.infinite)
            requestedIndex = clamp(requestedIndex, 0, slides.length - 1);
        event?.preventDefault();
        const { animate = true } = options;
        const lastIndex = slides.length - 1;
        const current = this.#current;
        let index = requestedIndex;
        if (requestedIndex < 0)
            index = lastIndex;
        else if (requestedIndex > lastIndex)
            index = 0;
        const isAdjacent = Math.abs(index - current) <= 1 && requestedIndex >= 0 && requestedIndex <= lastIndex;
        const instant = prefersReducedMotion() || !animate;
        // A jump of several slides, or a wrap, moves the target next to the current slide so the
        // scroll covers one slide, then restores the order and repositions instantly.
        if (!instant && !isAdjacent && this.#visibleSlides.length === 1) {
            this.#looping = true;
            await scroll.finished;
            const target = slides[index];
            if (!target || !currentSlide) {
                this.#looping = false;
                return;
            }
            const placeholder = document.createElement('slideshow-slide');
            target.before(placeholder);
            if (requestedIndex < current)
                currentSlide.before(target);
            else
                currentSlide.after(target);
            if (current === 0)
                scroll.to(currentSlide, { instant: true });
            queueMicrotask(async () => {
                await scroll.finished;
                this.#looping = false;
                placeholder.replaceWith(target);
                scroll.to(target, { instant: true });
                // Safari keeps the old scroll timeline after the reorder until the timeline is refreshed.
                requestAnimationFrame(() => {
                    this.setAttribute('refreshing-timeline', '');
                    requestAnimationFrame(() => this.removeAttribute('refreshing-timeline'));
                });
            });
        }
        const slide = slides[index];
        if (!slide)
            return;
        const previousIndex = this.#current;
        slide.setAttribute('aria-hidden', 'false');
        setTabStops(slide, true);
        scroll.to(slide, { instant });
        this.current = this.slides?.indexOf(slide) || 0;
        this.#centerSelectedThumbnail(index, instant ? 'instant' : 'smooth');
        this.#announce({ index, previousIndex, userInitiated: event != null, trigger: 'select', slide });
    }
    next(event, options) {
        event?.preventDefault();
        this.select(this.nextIndex, event, options);
    }
    previous(event, options) {
        event?.preventDefault();
        this.select(this.previousIndex, event, options);
    }
    /**
     * Starts autoplay. An `on:click` action passes its event instead of an interval, which selects
     * the `autoplay` interval.
     */
    play(interval) {
        if (this.#interval)
            return;
        const delay = typeof interval === 'number' ? interval : this.autoplayInterval;
        if (!delay)
            return;
        this.paused = false;
        this.#interval = window.setInterval(() => {
            if (this.matches(':hover') || document.hidden)
                return;
            this.next();
        }, delay);
    }
    /** Stops autoplay until the shopper plays it again. */
    pause() {
        this.paused = true;
        this.suspend();
    }
    /** Stops autoplay for now without recording a pause. */
    suspend() {
        window.clearInterval(this.#interval);
        this.#interval = undefined;
    }
    /** Restarts autoplay unless it is off or the shopper paused it. */
    resume() {
        if (!this.autoplay || this.paused)
            return;
        this.pause();
        this.play();
    }
    get #slideRefs() {
        return this.#refList('slides');
    }
    get #scroller() {
        const scroller = this.refs.scroller;
        if (!isHTMLElement(scroller))
            throw new Error('slideshow-component requires a scroller ref.');
        return scroller;
    }
    get #stepSize() {
        return this.#visibleSlides.length > 1 ? this.#visibleSlides.length : 1;
    }
    #refList(name) {
        const value = this.refs[name];
        return Array.isArray(value) ? value.filter(isHTMLElement) : [];
    }
    #resolveIndex(input) {
        if (typeof input === 'number')
            return input;
        if (typeof input === 'string')
            return parseInt(input, 10);
        const slide = this.#slideRefs.find((candidate) => candidate.getAttribute('slide-id') === input.id);
        const slides = this.slides;
        if (!slide || !slides)
            return undefined;
        if (slide.hasAttribute('hidden')) {
            slide.setAttribute('reveal', '');
            slide.setAttribute('aria-hidden', 'false');
            setTabStops(slide, true);
        }
        return this.slides?.indexOf(slide);
    }
    #announce(detail) {
        this.dispatchEvent(new SlideshowSelectEvent({ ...detail, id: detail.slide.getAttribute('slide-id') }));
    }
    #setupWithoutControls() {
        this.current = 0;
        if (this.hasAttribute('auto-hide-controls')) {
            const controls = this.refs.slideshowControls;
            if (isHTMLElement(controls))
                controls.hidden = true;
        }
        this.#slideRefs[0]?.setAttribute('aria-hidden', 'false');
        this.#updateProgress();
    }
    #setup(signal) {
        this.#setupIntersectionObserver();
        const scroller = this.#scroller;
        this.#scroll = new Scroller(scroller, {
            onScroll: this.#handleScroll,
            onScrollStart: () => this.setAttribute('transitioning', ''),
            onScrollEnd: () => {
                this.#updateVisibleSlides();
                this.removeAttribute('transitioning');
            },
        });
        scroller.addEventListener('mousedown', this.#handleMouseDown, { signal });
        // A mouse drag moves the strip, never a card's link or image: a native drag would end the strip's drag and, on
        // release, drop the link onto the page, which opens the card. Some browsers start one even when the press that
        // began it was cancelled.
        scroller.addEventListener('dragstart', this.#preventNativeDrag, { signal });
        scroller.addEventListener('scroll', this.#scheduleScrollState, { signal, passive: true });
        // A non-passive wheel listener holds back page scrolling, so only opted-in strips register one.
        if (this.hasAttribute('wheel'))
            scroller.addEventListener('wheel', this.#handleWheel, { signal, passive: false });
        this.addEventListener('keydown', this.#handleKeydown, { signal });
        this.#watchHiddenSlides(signal);
        this.addEventListener('mouseenter', () => this.suspend(), { signal });
        this.addEventListener('mouseleave', () => this.resume(), { signal });
        this.addEventListener('pointerenter', () => this.setAttribute('actioned', ''), { signal });
        this.addEventListener('focusin', this.#holdAutoplayForFocus, { signal });
        this.addEventListener('focusout', this.#releaseAutoplayFromFocus, { signal });
        document.addEventListener('visibilitychange', () => (document.hidden ? this.suspend() : this.resume()), { signal });
        this.#updateControlsVisibility();
        this.disabled = this.isNested || this.disabled;
        // Shoppers who reduce motion get a still slideshow with the play control available (WCAG 2.2.2).
        if (this.autoplay && prefersReducedMotion())
            this.paused = true;
        this.resume();
        this.current = this.initialSlideIndex;
        scheduler.schedule(() => {
            if (signal.aborted)
                return;
            let visibleSlideCount = 0;
            const initialSlideId = this.initialSlide?.getAttribute('slide-id');
            // Wait a frame so layout is final before the first positioning; Safari on mobile otherwise
            // races full-width sections.
            requestAnimationFrame(() => {
                if (this.initialSlideIndex !== 0 && initialSlideId) {
                    this.select({ id: initialSlideId }, undefined, { animate: false });
                    visibleSlideCount = 1;
                    return;
                }
                visibleSlideCount = this.#updateVisibleSlides();
                if (visibleSlideCount === 0) {
                    this.select(0, undefined, { animate: false });
                    visibleSlideCount = 1;
                }
            });
            this.#resizeObserver = new ResizeObserver(async () => {
                if (viewTransition.current)
                    await viewTransition.current;
                if (visibleSlideCount > 1)
                    this.#updateVisibleSlides();
                this.#syncStripFocus();
                this.#updateControlsVisibility();
                this.#scheduleScrollState();
            });
            this.#resizeObserver.observe(this.#refElement('slideshowContainer') ?? scroller);
        });
    }
    #refElement(name) {
        const value = this.refs[name];
        return isHTMLElement(value) ? value : undefined;
    }
    /**
     * Holds autoplay while keyboard focus is on a control or link inside the slideshow, so a slide
     * never moves away from the element the shopper is on (WCAG 2.2.2). Pointer focus does not hold
     * it; hovering already does. The play control restarts autoplay on request.
     */
    #holdAutoplayForFocus = (event) => {
        if (event.target instanceof Element && event.target.matches(':focus-visible'))
            this.suspend();
    };
    /** Restarts autoplay once focus has left the slideshow; a shopper's pause stays. */
    #releaseAutoplayFromFocus = (event) => {
        if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget))
            return;
        this.resume();
    };
    /** Keeps keyboard focus inside the visible part of the strip. */
    #handleFocusIn = (event) => {
        const target = event.target;
        if (this.#keyboardFocus || !(target instanceof Element) || !this.#scroll)
            return;
        const slides = this.slides ?? [];
        const index = slides.findIndex((slide) => slide.contains(target));
        const slide = slides[index];
        if (!slide)
            return;
        if (this.#inView(slide) && slide.getAttribute('aria-hidden') !== 'true')
            return;
        this.select(index, event, { animate: false });
    };
    /** Whether the whole slide lies inside the strip along its scroll axis. */
    #inView(slide) {
        const bounds = slide.getBoundingClientRect();
        const viewport = this.#scroller.getBoundingClientRect();
        return this.#scroll?.axis === 'y'
            ? bounds.top >= viewport.top - 1 && bounds.bottom <= viewport.bottom + 1
            : bounds.left >= viewport.left - 1 && bounds.right <= viewport.right + 1;
    }
    /**
     * Arrow keys step through slides: inside a slide they move focus to the neighbouring slide,
     * elsewhere (arrows, dots, the strip) they select the next or previous page. Home and End reach
     * the first and last slide. Keyboard selection never animates.
     */
    #handleKeydown = (event) => {
        const scroll = this.#scroll;
        if (!scroll || event.defaultPrevented || this.disabled)
            return;
        if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey)
            return;
        const target = event.target;
        if (!(target instanceof Element) || target.closest(ARROW_KEY_OWNERS))
            return;
        // A nested slideshow that slides on its own handles its keys first.
        const owner = target.closest('slideshow-component');
        if (owner !== this && owner instanceof _a && !owner.disabled)
            return;
        const step = this.#keyStep(event.key, scroll.axis);
        const slides = this.slides;
        if (step === undefined || !slides?.length)
            return;
        const last = slides.length - 1;
        const from = slides.findIndex((slide) => slide.contains(target));
        if (from === -1) {
            event.preventDefault();
            if (step === 'first')
                this.select(0, event, { animate: false });
            else if (step === 'last')
                this.select(last, event, { animate: false });
            else if (step === 1)
                this.next(event, { animate: false });
            else
                this.previous(event, { animate: false });
            return;
        }
        let index = step === 'first' ? 0 : step === 'last' ? last : from + step;
        if (this.infinite)
            index = (index + slides.length) % slides.length;
        const slide = slides[index];
        if (!slide || index === from)
            return;
        event.preventDefault();
        if (!this.#inView(slide))
            this.select(index, event, { animate: false });
        this.#keyboardFocus = true;
        const focusTarget = slide.querySelector(FOCUSABLE) ?? this.#scroller;
        focusTarget.focus({ preventScroll: true });
        this.#keyboardFocus = false;
    };
    /** The slide step a key asks for along the strip's axis, in reading direction. */
    #keyStep(key, axis) {
        if (key === 'Home')
            return 'first';
        if (key === 'End')
            return 'last';
        if (axis === 'y')
            return key === 'ArrowDown' ? 1 : key === 'ArrowUp' ? -1 : undefined;
        const forward = getDirectionMultiplier(this);
        if (key === 'ArrowRight')
            return forward;
        if (key === 'ArrowLeft')
            return forward === 1 ? -1 : 1;
        return undefined;
    }
    /**
     * Turns a vertical wheel into travel along the strip. A snapping strip moves one page per wheel
     * gesture; a free-scroll strip follows the wheel. At either end the event is left to the page.
     * Horizontal wheels and trackpad swipes already scroll the strip natively.
     */
    #handleWheel = (event) => {
        const scroll = this.#scroll;
        if (!scroll || this.disabled || event.ctrlKey || scroll.axis !== 'x')
            return;
        const scale = event.deltaMode === WheelEvent.DOM_DELTA_LINE
            ? WHEEL_LINE_HEIGHT
            : event.deltaMode === WheelEvent.DOM_DELTA_PAGE
                ? this.#scroller.clientWidth
                : 1;
        const delta = event.deltaY * scale;
        if (Math.abs(delta) <= Math.abs(event.deltaX * scale))
            return;
        const forward = delta > 0;
        if (this.#atScrollEnd(forward))
            return;
        event.preventDefault();
        window.clearTimeout(this.#wheelTimer);
        this.#wheelTimer = window.setTimeout(this.#endWheelGesture, WHEEL_GESTURE_GAP);
        if (this.freeScroll) {
            scroll.by(delta * getDirectionMultiplier(this.#scroller), { instant: true });
            return;
        }
        if (this.#wheelStepped)
            return;
        this.#wheelTravel += delta;
        if (Math.abs(this.#wheelTravel) < WHEEL_STEP_DISTANCE)
            return;
        this.#wheelStepped = true;
        const last = (this.slides?.length ?? 1) - 1;
        this.select(forward ? Math.min(this.nextIndex, last) : Math.max(this.previousIndex, 0), event);
    };
    #endWheelGesture = () => {
        this.#wheelTravel = 0;
        this.#wheelStepped = false;
        // Instant scrolls are not reported as shopper scrolls, so sync once the gesture ends.
        if (this.freeScroll)
            this.#handleScroll();
    };
    /** Whether the strip cannot travel further toward its end (`forward`) or its start. */
    #atScrollEnd(forward) {
        const { position, min, max } = this.#scrollRange();
        const travelled = Math.abs(position);
        return forward ? travelled >= max - min - 1 : travelled <= 1;
    }
    /**
     * Disables an arrow only when both the selection and the strip are at its end. The selection alone is not enough:
     * when every slide is partly visible, a page step already passes the last slide while the strip can still scroll.
     * The scroll alone is not either: a strip that does not scroll, as in a fade, still moves through its slides.
     */
    #updateArrows() {
        const { previous, next } = this.refs;
        const slides = this.slides;
        const bounded = !this.infinite && Boolean(slides?.length);
        const scrolls = Boolean(this.#scroll);
        if (previous instanceof HTMLButtonElement) {
            previous.disabled = bounded && this.#current <= 0 && (!scrolls || this.#atScrollEnd(false));
        }
        if (next instanceof HTMLButtonElement) {
            next.disabled = bounded && this.nextIndex >= (slides?.length ?? 0) && (!scrolls || this.#atScrollEnd(true));
        }
    }
    /** Updates the progress bar and the arrows once per frame while the strip scrolls or resizes. */
    #scheduleScrollState = () => {
        if (this.#progressFrame !== undefined)
            return;
        this.#progressFrame = requestAnimationFrame(() => {
            this.#progressFrame = undefined;
            this.#updateProgress();
            this.#updateArrows();
        });
    };
    /** Writes the scroll position and the visible share of the strip to the progress bar. */
    #updateProgress() {
        const bar = this.#refElement('progress');
        if (!bar)
            return;
        const scroller = this.#scroller;
        const vertical = this.#scroll?.axis === 'y';
        const length = vertical ? scroller.scrollHeight : scroller.scrollWidth;
        const visible = vertical ? scroller.clientHeight : scroller.clientWidth;
        const position = Math.abs(vertical ? scroller.scrollTop : scroller.scrollLeft);
        const span = length - visible;
        bar.style.setProperty('--slideshow-progress', `${span < 1 ? 0 : clamp(position / span, 0, 1)}`);
        bar.style.setProperty('--slideshow-progress-size', `${length > 0 ? clamp(visible / length, 0, 1) : 1}`);
    }
    /** Syncs the current slide with a shopper's scroll and announces a change. */
    #handleScroll = () => {
        const previousIndex = this.#current;
        const index = this.#sync();
        if (index === previousIndex)
            return;
        const slide = this.slides?.[index];
        if (slide)
            this.#announce({ index, previousIndex, userInitiated: true, trigger: 'scroll', slide });
    };
    /** Makes the visible slide nearest the strip's reference point current and returns its index. */
    #sync() {
        const slides = this.slides;
        if (!slides || !this.#scroll)
            return (this.current = 0);
        const visible = this.#visibleSlides;
        if (!visible.length)
            return this.#current;
        const { axis } = this.#scroll;
        const scroller = this.#scroller;
        const centers = visible.map((slide) => center(slide, axis));
        const reference = visible.length > 1 ? scroller.getBoundingClientRect()[axis] : center(scroller, axis);
        const nearest = visible[centers.indexOf(closest(centers, reference))];
        if (!nearest)
            return (this.current = 0);
        return (this.current = slides.indexOf(nearest));
    }
    #preventNativeDrag = (event) => {
        if (!this.disabled)
            event.preventDefault();
    };
    /** Drags the strip with the mouse; touch keeps the browser's native scrolling. */
    #handleMouseDown = (event) => {
        const slides = this.slides;
        const scroll = this.#scroll;
        if (!slides || slides.length <= 1 || !scroll)
            return;
        if (!(event.target instanceof Element))
            return;
        // Only the primary button drags; a context-menu click must not start one.
        if (event.button !== 0 || this.disabled || this.#dragging)
            return;
        // 3D models handle their own dragging.
        if (event.target.closest('model-viewer'))
            return;
        event.preventDefault();
        const { axis } = scroll;
        const start = axis === 'x' ? event.clientX : event.clientY;
        const controller = new AbortController();
        const { signal } = controller;
        const samples = [{ time: performance.now(), position: start }];
        let previous = start;
        let moved = false;
        let distance = 0;
        let overscroll = 0;
        this.#dragging = true;
        this.#scroller.removeAttribute('settling');
        const onPointerMove = (moveEvent) => {
            const position = axis === 'x' ? moveEvent.clientX : moveEvent.clientY;
            const initialDelta = start - position;
            if (!initialDelta)
                return;
            if (!moved) {
                moved = true;
                this.setPointerCapture(moveEvent.pointerId);
                // A nested slideshow at its end hands the drag to the slideshow around it.
                const parent = this.parentElement?.closest('slideshow-component');
                const nested = parent instanceof _a && parent !== this;
                const blocked = (initialDelta < 0 && this.atStart) || (initialDelta > 0 && this.atEnd);
                if (nested && blocked) {
                    controller.abort();
                    this.#dragging = false;
                    return;
                }
                // The press is a drag from here on, so the click that ends it is blocked now rather than on release: a drag
                // can also end by pointercancel or a lost capture, after which the release lands on the link under the
                // pointer without any pointerup reaching this strip.
                suppressNextClick();
                this.pause();
                this.setAttribute('dragging', '');
            }
            moveEvent.stopImmediatePropagation();
            const delta = previous - position;
            previous = position;
            distance += Math.abs(delta);
            samples.push({ time: performance.now(), position });
            overscroll = this.#dragBy(delta, overscroll);
        };
        const onPointerUp = async (upEvent) => {
            // Only this strip losing its capture ends its drag; another element's capture changes are not this drag's.
            if (upEvent.type === 'lostpointercapture' && upEvent.target !== this)
                return;
            controller.abort();
            this.#dragging = false;
            this.removeAttribute('dragging');
            if (this.hasPointerCapture(upEvent.pointerId))
                this.releasePointerCapture(upEvent.pointerId);
            this.#springBack();
            const velocity = overscroll ? 0 : releaseVelocity(samples, performance.now());
            const slides = this.slides;
            if (!slides?.length)
                return;
            if (this.freeScroll) {
                const previousIndex = this.#current;
                // A flick glides at most one strip length, so it never throws the shopper to the far end.
                const reach = axis === 'x' ? this.#scroller.clientWidth : this.#scroller.clientHeight;
                const glide = clamp(velocity * GLIDE_PER_VELOCITY, -reach, reach);
                if (Math.abs(velocity) >= DRAG_SETTLE_VELOCITY)
                    scroll.by(glide);
                await scroll.finished;
                if (this.#dragging)
                    return;
                const index = this.#sync();
                const slide = slides[index];
                if (slide && index !== previousIndex) {
                    this.#centerSelectedThumbnail(index);
                    this.#announce({ index, previousIndex, userInitiated: true, trigger: 'drag', slide });
                }
                this.resume();
                return;
            }
            const current = this.#current;
            const next = this.#sync();
            const settle = current !== next || Math.abs(velocity) < DRAG_SETTLE_VELOCITY || distance < DRAG_SETTLE_DISTANCE;
            const index = clamp(next + (settle ? 0 : Math.sign(velocity)), 0, slides.length - 1);
            const slide = slides[index];
            if (!slide)
                return;
            scroll.to(slide);
            this.#centerSelectedThumbnail(index);
            this.#announce({ index, previousIndex: this.#current, userInitiated: true, trigger: 'drag', slide });
            this.current = index;
            await scroll.finished;
            // The shopper may have started another drag before the scroll settled.
            if (this.#dragging)
                return;
            scroll.snap = true;
            this.resume();
        };
        scroll.snap = false;
        document.addEventListener('pointermove', onPointerMove, { signal });
        document.addEventListener('pointerup', onPointerUp, { signal });
        // The zoom dialog's first tap-and-drag can arrive as a new pointerdown; end this drag so it
        // does not change slides.
        document.addEventListener('pointerdown', onPointerUp, { signal });
        document.addEventListener('pointercancel', onPointerUp, { signal });
        document.addEventListener('lostpointercapture', onPointerUp, { signal });
    };
    /**
     * Scrolls by a drag delta. Travel past either end is not scrolled but accumulated as overscroll,
     * which stretches the strip with resistance; dragging back relaxes the stretch before scrolling.
     * Returns the new overscroll in physical scroll pixels.
     */
    #dragBy(delta, overscroll) {
        let remaining = delta;
        let stretched = overscroll;
        if (stretched && Math.sign(remaining) !== Math.sign(stretched)) {
            const relaxed = Math.sign(remaining) * Math.min(Math.abs(remaining), Math.abs(stretched));
            stretched += relaxed;
            remaining -= relaxed;
        }
        if (remaining) {
            const { position, min, max } = this.#scrollRange();
            const target = position + remaining;
            const excess = target < min ? target - min : target > max ? target - max : 0;
            if (remaining !== excess)
                this.#scroll?.by(remaining - excess, { instant: true });
            stretched += excess;
        }
        this.#stretch(stretched);
        return stretched;
    }
    /** The physical scroll position and its limits; in right-to-left strips the range is negative. */
    #scrollRange() {
        const scroller = this.#scroller;
        if (this.#scroll?.axis === 'y') {
            return { position: scroller.scrollTop, min: 0, max: scroller.scrollHeight - scroller.clientHeight };
        }
        const span = scroller.scrollWidth - scroller.clientWidth;
        const rightToLeft = getDirectionMultiplier(scroller) === -1;
        return { position: scroller.scrollLeft, min: rightToLeft ? -span : 0, max: rightToLeft ? 0 : span };
    }
    /** Offsets the strip against an overscroll, with resistance and a limit; reduced motion keeps it still. */
    #stretch(overscroll) {
        if (prefersReducedMotion())
            return;
        const offset = -Math.sign(overscroll) * Math.min(EDGE_STRETCH_LIMIT, Math.abs(overscroll) * EDGE_RESISTANCE);
        this.#scroller.style.translate = !offset ? '' : this.#scroll?.axis === 'y' ? `0 ${offset}px` : `${offset}px 0`;
    }
    /** Returns a stretched strip to rest through the `settling` transition. */
    #springBack() {
        const scroller = this.#scroller;
        if (!scroller.style.translate)
            return;
        scroller.setAttribute('settling', '');
        scroller.style.translate = '';
        scroller.addEventListener('transitionend', () => scroller.removeAttribute('settling'), { once: true });
    }
    /**
     * With `auto-hide-controls`, hides the controls (`slideshowControls`, such as dots or a progress bar) and arrows
     * kept outside them (`arrows`, such as a title row's) while every slide fits and the strip has nowhere to go.
     */
    #updateControlsVisibility() {
        if (!this.hasAttribute('auto-hide-controls'))
            return;
        const scroller = this.#scroller;
        const fits = scroller.scrollWidth <= scroller.offsetWidth;
        for (const name of ['slideshowControls', 'arrows']) {
            const element = this.refs[name];
            if (isHTMLElement(element))
                element.hidden = fits;
        }
    }
    #setupIntersectionObserver() {
        const slides = this.#slideRefs;
        if (!slides.length)
            return;
        this.#intersectionObserver?.disconnect();
        const observer = new IntersectionObserver((entries) => {
            for (const entry of [...entries, ...observer.takeRecords()]) {
                if (!isHTMLElement(entry.target))
                    continue;
                const slide = entry.target;
                const known = this.#visibleSlides.indexOf(slide);
                const visible = entry.intersectionRatio >= SLIDE_VISIBILITY_THRESHOLD;
                if (visible && known === -1)
                    this.#visibleSlides.push(slide);
                else if (!visible && known > -1)
                    this.#visibleSlides.splice(known, 1);
            }
            this.#visibleSlides.sort((a, b) => slides.indexOf(a) - slides.indexOf(b));
            this.#updateVisibleSlides();
            // The page step follows the visible count, so the arrows' end state does too.
            this.#updateArrows();
        }, 
        // The margin absorbs sub-pixel rendering.
        { root: this.#scroller, threshold: SLIDE_VISIBILITY_THRESHOLD, rootMargin: '1px' });
        this.#intersectionObserver = observer;
        for (const slide of slides)
            observer.observe(slide);
    }
    #centerSelectedThumbnail(index, behavior = 'smooth') {
        const thumbnail = this.#refList('thumbnails')[index];
        const container = this.#refElement('thumbnailsContainer');
        if (!thumbnail || !container || !this.#refElement('slideshowControls'))
            return;
        scrollIntoView(thumbnail, { ancestor: container, behavior, block: 'center', inline: 'center' });
    }
    /**
     * Hides slides outside the strip from assistive technology and returns the visible count. A hidden slide holds no
     * tab stop, so nothing focusable sits inside an `aria-hidden` slide, and the strip itself takes focus when no
     * visible slide has anything to tab to.
     */
    #updateVisibleSlides() {
        const slides = this.slides;
        if (!slides?.length)
            return 0;
        // No slide meets the threshold while the host is mid-animation or has no layout, as in an
        // opening dialog. Hiding every slide then would leave nothing to read.
        const visible = this.#visibleSlides;
        if (!visible.length)
            return 0;
        scheduler.schedule(() => {
            for (const slide of slides) {
                const hidden = !visible.includes(slide);
                slide.setAttribute('aria-hidden', `${hidden}`);
                setTabStops(slide, !hidden);
            }
            this.#syncStripFocus();
        });
        return visible.length;
    }
    /** Keeps content that arrives in a hidden slide (a morph, a late render) out of the tab sequence too. */
    #watchHiddenSlides(signal) {
        const observer = new MutationObserver(() => {
            for (const slide of this.#slideRefs)
                if (slide.getAttribute('aria-hidden') === 'true')
                    setTabStops(slide, false);
            this.#syncStripFocus();
        });
        observer.observe(this.#scroller, { childList: true, subtree: true });
        signal.addEventListener('abort', () => observer.disconnect(), { once: true });
    }
    /**
     * A strip that scrolls but holds nothing to tab to cannot be reached with the keyboard (WCAG 2.1.1), which is the
     * case for a product card's gallery, whose images are not controls. It then takes focus itself, named by
     * `data-focus-label`, and the arrow keys move it as they do from the arrow buttons, which leave the tab sequence
     * (a card then costs one stop for its images, not three) and stay one tap or one screen reader gesture away. A
     * strip with something to tab to in view, one that is turned off and one that does not scroll stays out of the
     * sequence and keeps its buttons in it.
     */
    #syncStripFocus() {
        const scroller = this.#scroller;
        const label = scroller.dataset.focusLabel;
        const vertical = this.#scroll?.axis === 'y';
        const scrolls = vertical ? scroller.scrollHeight > scroller.clientHeight : scroller.scrollWidth > scroller.clientWidth;
        const reachable = this.#visibleSlides.some((slide) => slide.querySelector(TAB_STOPS));
        const focusable = Boolean(label) && scrolls && !this.disabled && !reachable;
        scroller.tabIndex = focusable ? 0 : -1;
        if (focusable && label) {
            scroller.setAttribute('role', 'group');
            scroller.setAttribute('aria-label', label);
        }
        else {
            scroller.removeAttribute('role');
            scroller.removeAttribute('aria-label');
        }
        for (const control of this.#refElement('slideshowContainer')?.querySelectorAll(':scope > slideshow-arrows .slideshow-control') ?? []) {
            holdTabStop(control, scroller, focusable);
        }
    }
}
_a = Slideshow;
if (!customElements.get('slideshow-component')) {
    customElements.define('slideshow-component', Slideshow);
}
